import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FaChevronDown, FaChevronRight, FaCopy, FaPen } from "react-icons/fa";
import MathText from "../math/MathText";

const questionHeadingPattern = /^##\s*第\s*([^\s#]+)\s*題.*$/gm;
const sectionPattern = /^(題目|解題思路|詳解|檢查|答案)：\s*(.*)$/;

export default function SolutionReview({ value, onEdit, onCopy }) {
  const questions = useMemo(() => parseSolution(value), [value]);
  const [openKeys, setOpenKeys] = useState(new Set());

  useEffect(() => {
    setOpenKeys(new Set(questions.length === 1 ? [questions[0].key] : []));
  }, [questions]);

  if (!value.trim()) {
    return (
      <EmptyState>
        <strong>尚未產生詳解</strong>
        <span>可以先解析或輸入題目，再按 AI 寫詳解。</span>
      </EmptyState>
    );
  }

  if (!questions.length) {
    return (
      <RawPreview>
        <PreviewToolbar>
          <span>詳解預覽</span>
          <SmallButton type="button" onClick={onEdit}>
            <FaPen /> 編輯
          </SmallButton>
        </PreviewToolbar>
        <RawText>
          <MathText source={value} fallback="" />
        </RawText>
      </RawPreview>
    );
  }

  const toggleQuestion = (key) => {
    setOpenKeys((current) => {
      const next = new Set(current);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const scrollToQuestion = (key) => {
    document.getElementById(key)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <ReviewShell>
      <QuestionNav aria-label="詳解題號導覽">
        {questions.map((question) => (
          <QuestionNavButton
            key={question.key}
            type="button"
            aria-label={question.navAriaLabel}
            title={question.navAriaLabel}
            onClick={() => scrollToQuestion(question.key)}
          >
            {question.navLabel}
          </QuestionNavButton>
        ))}
      </QuestionNav>

      <QuestionList>
        {questions.map((question) => {
          const isOpen = openKeys.has(question.key);
          return (
            <QuestionCard key={question.key} id={question.key}>
              <QuestionHeader>
                <QuestionSummaryButton type="button" onClick={() => toggleQuestion(question.key)}>
                  {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                  <strong>{question.heading}</strong>
                  {question.originalLabel && (
                    <OriginalNumberBadge>{question.originalLabel}</OriginalNumberBadge>
                  )}
                </QuestionSummaryButton>
                <AnswerSummary>
                  <MathText source={question.answer || "答案待確認"} />
                </AnswerSummary>
              </QuestionHeader>

              {isOpen && (
                <QuestionBody>
                  {question.sections.map((section) => (
                    <SectionBlock key={section.label}>
                      <SectionLabel>{section.label}</SectionLabel>
                      <SectionContent content={section.content} />
                    </SectionBlock>
                  ))}
                  <CardActions>
                    <SmallButton type="button" onClick={() => onCopy(question.raw)}>
                      <FaCopy /> 複製本題
                    </SmallButton>
                    <SmallButton type="button" onClick={onEdit}>
                      <FaPen /> 編輯全部
                    </SmallButton>
                  </CardActions>
                </QuestionBody>
              )}
            </QuestionCard>
          );
        })}
      </QuestionList>
    </ReviewShell>
  );
}

function parseSolution(value) {
  const cleaned = stripCodeFence(value);
  const matches = [...cleaned.matchAll(questionHeadingPattern)];
  if (!matches.length) return [];

  const rawQuestions = matches.map((match, index) => {
    const start = match.index;
    const end = index + 1 < matches.length ? matches[index + 1].index : cleaned.length;
    const raw = cleaned.slice(start, end).trim();
    const body = cleaned.slice(match.index + match[0].length, end).trim();
    const sections = parseSections(body);
    return {
      key: `solution-question-${index}-${match[1]}`,
      number: match[1],
      raw,
      sections,
      answer: extractAnswer(sections),
    };
  });

  return decorateQuestionNumbers(rawQuestions);
}

function stripCodeFence(value) {
  return value
    .replace(/^\s*```(?:markdown)?\s*$/gm, "")
    .replace(/^\s*```\s*$/gm, "")
    .trim();
}

function parseSections(body) {
  const sections = [];
  let current = { label: "內容", lines: [] };

  body.split("\n").forEach((line) => {
    const match = line.match(sectionPattern);
    if (match) {
      pushSection(sections, current);
      current = { label: match[1], lines: match[2] ? [match[2]] : [] };
      return;
    }
    current.lines.push(line);
  });

  pushSection(sections, current);
  return sections;
}

function pushSection(sections, section) {
  const content = section.lines.join("\n").trim();
  if (content) sections.push({ label: section.label, content });
}

function extractAnswer(sections) {
  const answer = sections.find((section) => section.label === "答案")?.content || "";
  return answer
    .split("\n")
    .map((line) => line.replace(/^[-*]\s*/, "").trim())
    .filter(Boolean)
    .slice(0, 2)
    .join(" ");
}

function decorateQuestionNumbers(questions) {
  const numberCounts = questions.reduce((counts, question) => {
    counts.set(question.number, (counts.get(question.number) || 0) + 1);
    return counts;
  }, new Map());
  const hasDuplicateNumbers = [...numberCounts.values()].some((count) => count > 1);

  return questions.map((question, index) => {
    const ordinal = index + 1;
    const originalNumber = String(question.number || "").trim();
    const originalDiffersFromOrdinal = originalNumber && originalNumber !== String(ordinal);
    const shouldUseOrdinal = hasDuplicateNumbers;
    const navLabel = shouldUseOrdinal ? String(ordinal) : originalNumber || String(ordinal);
    const heading = shouldUseOrdinal
      ? `第 ${ordinal} 題`
      : `第 ${originalNumber || ordinal} 題`;
    const originalLabel =
      shouldUseOrdinal && originalDiffersFromOrdinal ? `原題號 ${originalNumber}` : "";
    const navAriaLabel = originalLabel
      ? `前往第 ${ordinal} 題，${originalLabel}`
      : `前往${heading}`;

    return {
      ...question,
      ordinal,
      navLabel,
      heading,
      originalLabel,
      navAriaLabel,
    };
  });
}

function SectionContent({ content }) {
  return (
    <ContentFlow>
      <MathText source={content} fallback="" />
    </ContentFlow>
  );
}

const ReviewShell = styled.div`
  display: grid;
  gap: 12px;
`;

const QuestionNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  overflow: visible;
  padding: 4px 0 10px;
`;

const QuestionNavButton = styled.button`
  width: 44px;
  min-width: 44px;
  height: 34px;
  flex: 0 0 auto;
  border: 1px solid var(--app-border, #cfcfcf);
  border-radius: 8px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #1f1f1f);
  font-weight: 750;
  cursor: pointer;
`;

const QuestionList = styled.div`
  display: grid;
  gap: 10px;
`;

const QuestionCard = styled.article`
  scroll-margin-top: 18px;
  border: 1px solid var(--app-border, #d4d4d4);
  border-radius: 8px;
  background: var(--app-surface, #fff);
  overflow: hidden;
`;

const QuestionHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(130px, auto) minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: var(--app-surface-hover, #f3f3f3);

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

const QuestionSummaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--app-surface-text, #1f1f1f);
  cursor: pointer;
  text-align: left;

  svg {
    font-size: 12px;
    color: var(--app-muted-text, #555);
  }
`;

const OriginalNumberBadge = styled.span`
  border-radius: 999px;
  padding: 2px 7px;
  background: var(--app-input-bg, #fff);
  color: var(--app-muted-text, #626262);
  font-size: 12px;
  font-weight: 750;
  white-space: nowrap;
`;

const AnswerSummary = styled.div`
  color: var(--app-subtle-text, #333);
  font-size: 14px;
  line-height: 1.45;
  overflow-wrap: anywhere;
`;

const QuestionBody = styled.div`
  display: grid;
  gap: 14px;
  padding: 14px;
`;

const SectionBlock = styled.section`
  display: grid;
  gap: 7px;
`;

const SectionLabel = styled.h4`
  margin: 0;
  color: var(--app-surface-text, #171717);
  font-size: 14px;
  font-weight: 800;
`;

const ContentFlow = styled.div`
  margin: 0;
  color: var(--app-subtle-text, #2b2b2b);
  font-size: 14px;
  line-height: 1.75;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
  word-break: break-word;
`;

const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 4px;
`;

const SmallButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--app-border, #c9c9c9);
  border-radius: 8px;
  padding: 8px 10px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #333);
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;
`;

const EmptyState = styled.div`
  display: grid;
  gap: 4px;
  min-height: 180px;
  place-content: center;
  border: 1px dashed var(--app-border, #cfcfcf);
  border-radius: 8px;
  background: var(--app-surface-hover, #fafafa);
  color: var(--app-muted-text, #686868);
  text-align: center;

  strong {
    color: var(--app-surface-text, #262626);
  }
`;

const RawPreview = styled.div`
  display: grid;
  gap: 8px;
  border: 1px solid var(--app-border, #d4d4d4);
  border-radius: 8px;
  padding: 12px;
  background: var(--app-surface, #fff);
`;

const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-weight: 750;
`;

const RawText = styled.div`
  margin: 0;
  max-height: 420px;
  overflow: auto;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.6;
`;
